import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Divider, message, Alert } from "antd";
import MainWrapper from "../components/MainWrapper";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import SuccessMessage from "../components/SuccessMessage";
import SpinerIndicatorWhiteBg from "../components/SpinerIndicatorWhiteBg";
import { checkDuitNowQrTransaction, postMyrFPXCreateTransaction } from "../redux/actions/postQrInitManual";
import { getVerifyManualTransactionMyr } from "../redux/actions/getVerifyManualTransaction";
import DuitNowLogo from "../assets/DuitNowQR_Primary.png";
import expiredQr from "../assets/expiredQr_eng.png";
import { toLocaleStringFormate } from "../helpingFunctions";
const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  position: absolute;
  top: 10px
`;

const CardWrapper = styled.div`
  margin-top: 50px; 
  margin-bottom: 20px;
  width: 380px;
  background-color: white;
  border-radius: 15px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);

  @media (max-width: 400px) {
    width: 90%;
  }

  .logo-container {
    margin-bottom: 12px;
    width: 100px;
    height: 100px;
    img {
      height: 60px;
    }
  }

  .qr-container {
    border: 8px solid #ed2c5a;
    border-radius: 12px;
    padding: 10px;
    width: 100%;
    margin-bottom: 12px;
    position: relative;
    
    
  }
    .qr-footer {
        background-color: #ed2c5a;
        color: white;
        text-align: center;
        padding: 10px;
        font-weight: bold;
        margin: 10px -10px -10px -10px;
        border-radius: 0 0 4px 4px;
        font-size: 1rem;
    }
  .instructions {
    text-align: center;
    font-size: 12px;
    color: #333;
    margin-bottom: 16px;
  }

  .ref-id {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
  }

  .amount {
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 12px;
  }

  .expiry {
    color: #ed2c5a;
    font-size: 0.7rem;
    margin-bottom: 5px;
  }

  .timer {
    color: #ed2c5a;
    font-size: 1.5rem;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .error {
    color: red;
    font-size: 14px;
    font-weight: bold;
    margin-bottom: 20px;
  }

  .footer-text {
    font-size: 12px;
    text-align: center;
    color: #333;
    
    a {
        color: #333;
        text-decoration: underline;
        font-weight: bold;
    }
  }
`;

const DuitNowQrCardInfo = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const pollingRef = useRef(null);

    const { data: verifyData, error: verifyError } = useSelector((state) => state.getVerifyManualTransactionReducer);
    const { data: qrInitData, isLoading: qrInitLoading, error } = useSelector((state) => state.postQrInitManualReducer);
    const { data: trnxCheckData, error: trnxCheckError } = useSelector((state) => state.getQrManualCheckReducer);


    const [timeLeft, setTimeLeft] = useState(300); // Initial 5 minutes fallback
    const [isPaid, setIsPaid] = useState(false);

    useEffect(() => {
        if (!verifyData) {
            navigate(`/myr/${params.id}`);
        }
    }, [verifyData]);

    useEffect(() => {
        if (verifyData) {
            const myrTransactionParams = {
                transactionId: verifyData.trxRef,
                type: "duitnowqr",
                amount: parseFloat(verifyData.amount),
                sessionId: verifyData.sessionToken,
                userId: verifyData.merchantId,
            };
            dispatch(postMyrFPXCreateTransaction(myrTransactionParams, navigate, params.id, params.method, true));
        }
    }, [verifyData]);

    useEffect(() => {
        if (verifyError || error || qrInitLoading || !qrInitData || !verifyData?.trxRef) return;

        const expiryKey = `qr_expiry_${verifyData.trxRef}`;
        let expiryTime = localStorage.getItem(expiryKey);

        if (!expiryTime) {
            expiryTime = Date.now() + 300 * 1000;
            localStorage.setItem(expiryKey, expiryTime);
        } else {
            expiryTime = parseInt(expiryTime, 10);
        }

        const updateTimer = () => {
            const now = Date.now();
            const remaining = Math.max(0, Math.floor((expiryTime - now) / 1000));
            setTimeLeft(remaining);
            
            if (remaining === 0) {
                localStorage.removeItem(expiryKey);
            }
        };

        updateTimer();
        const timer = setInterval(updateTimer, 1000);

        return () => {
            clearInterval(timer);
        };
    }, [verifyError, error, qrInitLoading, qrInitData, verifyData?.trxRef]);

    useEffect(() => {
        const status = trnxCheckData?.status;
        if (!verifyData?.trxRef || isPaid || status === "03_Rejected" || status === "FAILED") {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
            return;
        }

        const pollStatus = () => {
            dispatch(checkDuitNowQrTransaction(verifyData.trxRef));
        };

        if (!pollingRef.current) {
            pollStatus(); // Initial check
            pollingRef.current = setInterval(pollStatus, 5000);
        }

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [verifyData, isPaid, timeLeft === 0, trnxCheckData?.status, dispatch]);

    useEffect(() => {
        if (trnxCheckData?.status === "SUCCESS" || trnxCheckData?.status === "PAID") {
            setIsPaid(true);
            if (pollingRef.current) clearInterval(pollingRef.current);

            // Redirect after 3 seconds
            setTimeout(() => {
                const expiryKey = `qr_expiry_${verifyData?.trxRef}`;
                localStorage.removeItem(expiryKey);

                const redirectUrl = localStorage.getItem("redirectionUrl") || verifyData?.redirectUrl || verifyData?.siteUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
            }, 5000);
        }
    }, [trnxCheckData, verifyData]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    };

    return (
        <Container>
            {qrInitLoading ? <SpinerIndicatorWhiteBg /> :
                <CardWrapper>
                    <div className="logo-container">
                        <img src={DuitNowLogo} style={{width:'100%', height: '100%'}} alt="DuitNow QR" />
                    </div>

                    {isPaid ? (
                        <div style={{ width: '100%', textAlign: 'center' }}>
                            <SuccessMessage />
                            <h3 style={{ color: "green", marginTop: 20 }}>Payment Successful</h3>
                            <p>Redirecting you back...</p>
                        </div>
                    ) : (
                        <> 
                                {error || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ? null :
                                    <div style={{padding: 4, borderRadius: 6, background: 'rgb(237 44 90 / 9%)', marginBottom: 6}}>
                                        <p className="instructions mb-0" style={{color:'red'}}>
                                            Please do not close or refresh this browser screen before payment is completed.
                                        </p>
                                    </div>
                                }
                                <div className="qr-container">
                                {!qrInitData?.qrCode || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ? (
                                    <img src={expiredQr} alt="Expired" width="100%" />
                                ) : (
                                    <img
                                        src={qrInitData?.qrCode?.startsWith('data:') ? qrInitData?.qrCode : `data:image/png;base64,${qrInitData?.qrCode}`}
                                        alt="Payment QR"
                                        width="100%"
                                    />
                                )}
                                <div className="qr-footer">MALAYSIA NATIONAL QR</div>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                {error || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ? null :
                                    <p className="instructions">
                                        If paying from mobile, screenshot the QR code above and select ‘Scan from Gallery’ from your bank app
                                    </p>
                                }

                                <p className="instructions">
                                    *QR can be scanned <b>one time only</b>
                                </p>

                                <div className="ref-id">REF: {verifyData?.trxRef}</div>
                                <div className="amount">RM {toLocaleStringFormate(verifyData?.amount)}</div>
                                
                                {error || timeLeft === 0 || trnxCheckData?.status === 'FAILED' ?
                                    <p className="error">
                                        {error ? error.split(':')[1] : "Transaction Expired"}
                                    </p>
                                    :
                                    <div>
                                        <div className="expiry">QR Code expires in</div>
                                        <div className="timer">{formatTime(timeLeft)}</div>
                                    </div>
                                }

                                <div className="footer-text">
                                    Click <a href={verifyData?.redirectUrl || verifyData?.siteUrl}>here</a> if you’re not redirected back to the merchant page after successful payment
                                </div>
                            </div>
                        </>
                    )}
                </CardWrapper>
            }
        </Container>
    );
};

export default DuitNowQrCardInfo;
